<script setup>
import { Head, Link } from '@inertiajs/vue3'
import {
  Search, MessageSquare, CreditCard, Video, LineChart,
  GraduationCap, ShieldCheck, Award, CalendarCheck, Radio, UserRound,
  CheckCircle2, ChevronRight,
} from 'lucide-vue-next'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHero from '@/Components/Marketing/PageHero.vue'
import { Button } from '@/Components/ui/button'
import { tutors, faq, becomeATutor } from '@/routes/index'
import { formatMoney } from '@/lib/money'

/**
 * How Bindle works, as a page rather than a jump link.
 *
 * "How it works" was an anchor to a section of the homepage, so the primary
 * secondary action in the hero moved the page a few hundred pixels and landed on
 * a two-card comparison. It is the second most-clicked thing a first-time
 * visitor reaches for; it deserves an answer.
 *
 * Three passes at the same story, in increasing depth: the four steps, what
 * happens inside a lesson, and what we check before a tutor is allowed near a
 * student. Every figure comes from the platform.
 */
const props = defineProps({
  figures: { type: Object, default: () => ({}) },
})

const steps = [
  {
    icon: Search,
    n: '01',
    title: 'Find a tutor who has done it',
    body: 'Filter by subject, level and exam board. Every profile leads with where they studied, what they read and the grades they achieved themselves — not a self-written summary of how passionate they are.',
    aside: 'No account needed to browse.',
  },
  {
    icon: MessageSquare,
    n: '02',
    title: 'Ask before you commit',
    body: 'Message the tutor through Bindle and describe what your child is stuck on. What comes back tells you more about their teaching than any profile: you are looking for someone who asks what has already been tried.',
    aside: 'No card required to start a conversation.',
  },
  {
    icon: CreditCard,
    n: '03',
    title: 'Agree a time and subscribe',
    body: 'Pick the plan that matches how much contact you want, agree a weekly slot with the tutor, and subscribe. Each lesson is paid for 48 hours before it, with no joining fee and no minimum term.',
    aside: 'Cancel from your account in one step.',
  },
  {
    icon: LineChart,
    n: '04',
    title: 'Watch it work',
    body: 'Lessons appear on a calendar you can subscribe to. The tutor records what was covered and what was set, and every payment produces an invoice — so the account holder can see progress without sitting in the room.',
    aside: 'Change or stop whenever you need to.',
  },
]

const inTheLesson = [
  {
    icon: Video,
    title: 'In the browser, nothing to install',
    body: 'The lesson opens from the schedule in your account at the agreed time. Shared whiteboard, screen sharing and a chat panel — on a laptop, a tablet or a phone.',
  },
  {
    icon: CalendarCheck,
    title: 'Weekly by default',
    body: 'Tutors set the length of a lesson and the hours they are available. Most students keep the same slot each week; extra sessions are arranged with the tutor directly.',
  },
  {
    icon: MessageSquare,
    title: 'Questions between lessons',
    body: 'Messaging stays inside Bindle, so a question on a Tuesday evening does not need a phone number and the account holder can read the thread.',
  },
]

const checks = [
  { icon: GraduationCap, title: 'Degree verified', body: 'We confirm the university, the course and the classification.' },
  { icon: Award, title: 'Their own grades verified', body: 'A tutor who cannot evidence the results they are teaching towards does not teach here.' },
  { icon: MessageSquare, title: 'Interviewed', body: 'A conversation about how they would teach a struggling student, not about their CV.' },
  { icon: ShieldCheck, title: 'DBS checked', body: 'A current check is a condition of being listed. No exceptions.' },
]
</script>

<template>
  <Head>
    <title>How Bindle works — from shortlist to first lesson</title>
    <meta
      name="description"
      content="How tutoring on Bindle works: find a grade-verified tutor, message them free, subscribe weekly, and follow progress from your account."
    />
  </Head>

  <AppLayout>
    <PageHero
      eyebrow="How it works"
      title="Four steps, and no surprises in any of them."
      lead="Bindle is a short list of tutors who have already achieved what your child is working towards, and a straightforward way to book them by the week. Here is the whole thing, start to finish."
    >
      <template #actions>
        <Link :href="tutors()"><Button size="lg">Find your tutor</Button></Link>
        <Link :href="faq()"><Button variant="secondary" size="lg">Read the FAQ</Button></Link>
      </template>
    </PageHero>

    <!-- ==================== THE FOUR STEPS ==================== -->
    <section class="section">
      <div class="container-page">
        <ol class="grid gap-5 md:grid-cols-2">
          <li
            v-for="(step, index) in steps"
            :key="step.n"
            v-reveal="{ delay: index * 70 }"
            class="surface-card flex gap-5 p-6 md:p-7"
          >
            <div class="flex flex-col items-center gap-3">
              <span class="grid size-12 shrink-0 place-items-center rounded-full bg-brand-soft text-brand">
                <component :is="step.icon" class="size-6" />
              </span>
              <span class="font-display text-sm font-bold text-brand/40">{{ step.n }}</span>
            </div>
            <div>
              <h2 class="text-xl">{{ step.title }}</h2>
              <p class="mt-2.5 text-[15px] leading-relaxed text-muted">{{ step.body }}</p>
              <p class="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                <CheckCircle2 class="size-4" /> {{ step.aside }}
              </p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- ==================== THE TWO WAYS ==================== -->
    <section class="border-y border-line bg-surface-2 py-14 md:py-20">
      <div class="container-page">
        <h2 class="max-w-2xl text-2xl md:text-3xl">Two ways to learn</h2>
        <p class="mt-3 max-w-2xl text-[15px] leading-relaxed text-muted">
          One is available today. The other is being built, and is marked as such everywhere it appears.
        </p>

        <div class="mt-8 grid gap-5 lg:grid-cols-2">
          <article v-reveal class="surface-card flex flex-col p-7">
            <span class="grid size-13 place-items-center rounded-full bg-brand-soft text-brand">
              <UserRound class="size-6" />
            </span>
            <h3 class="mt-5 font-sans text-lg font-bold uppercase tracking-[0.1em] text-brand-soft-text">
              1:1 Tutoring
            </h3>
            <p class="mt-3 text-[15px] leading-relaxed text-muted">
              A tutor you choose, at a time you agree, working on what your child is actually stuck on.
              This is what Bindle sells today.
            </p>
            <p v-if="figures.one_to_one_from" class="mt-5 text-sm text-muted">
              From
              <strong class="font-display text-3xl font-bold text-brand">{{ formatMoney(figures.one_to_one_from) }}</strong>
              /week
            </p>
            <Link :href="tutors()" class="mt-auto pt-6">
              <Button class="w-full sm:w-auto">Browse tutors</Button>
            </Link>
          </article>

          <article v-reveal="{ delay: 90 }" class="flex flex-col rounded-xl border border-dashed border-line-strong bg-surface p-7">
            <div class="flex items-start justify-between gap-3">
              <span class="grid size-13 place-items-center rounded-full bg-live-soft text-live-soft-text">
                <Radio class="size-6" />
              </span>
              <span class="rounded-full bg-live-soft px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-live-soft-text">
                Not available yet
              </span>
            </div>
            <h3 class="mt-5 font-sans text-lg font-bold uppercase tracking-[0.1em] text-live-soft-text">
              Bindle Live
            </h3>
            <p class="mt-3 text-[15px] leading-relaxed text-muted">
              A weekly live masterclass following a structured course, recorded into your library so a
              missed week is not a lost week. In development — it cannot be bought today.
            </p>
            <p class="mt-5 text-sm text-muted">
              Planned from <strong class="font-display text-3xl font-bold text-live-soft-text">£1.99</strong> /week
            </p>
            <Button variant="live" class="mt-auto w-full sm:w-auto" disabled aria-disabled="true">
              Coming soon!
            </Button>
          </article>
        </div>
      </div>
    </section>

    <!-- ==================== INSIDE A LESSON ==================== -->
    <section class="section">
      <div class="container-page">
        <h2 class="max-w-2xl text-2xl md:text-3xl">What a lesson is actually like</h2>
        <div class="mt-8 grid gap-5 md:grid-cols-3">
          <article
            v-for="(item, index) in inTheLesson"
            :key="item.title"
            v-reveal="{ delay: index * 80 }"
            class="surface-card p-6"
          >
            <span class="grid size-11 place-items-center rounded-full bg-brand-soft text-brand">
              <component :is="item.icon" class="size-5.5" />
            </span>
            <h3 class="mt-4 text-lg">{{ item.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-muted">{{ item.body }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- ==================== THE CHECKS ==================== -->
    <section class="border-t border-line bg-surface-2 py-14 md:py-20">
      <div class="container-page grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center">
        <div>
          <h2 class="text-2xl md:text-3xl">The part that happens before you ever see a tutor</h2>
          <p class="mt-3 max-w-xl text-[15px] leading-relaxed text-muted">
            Anyone can write “experienced tutor” on a profile. The work that makes Bindle worth using is
            the work you never see — deciding who is allowed on the list at all.
          </p>
          <Link :href="faq()" class="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
            Read the full FAQ <ChevronRight class="size-4" />
          </Link>
        </div>

        <ul class="grid gap-4 sm:grid-cols-2">
          <li
            v-for="(check, index) in checks"
            :key="check.title"
            v-reveal="{ delay: index * 70 }"
            class="rounded-xl border border-line bg-surface p-5"
          >
            <span class="grid size-10 place-items-center rounded-full bg-brand-soft text-brand">
              <component :is="check.icon" class="size-5" />
            </span>
            <h3 class="mt-3.5 text-base">{{ check.title }}</h3>
            <p class="mt-1.5 text-sm leading-relaxed text-muted">{{ check.body }}</p>
          </li>
        </ul>
      </div>
    </section>

    <!-- ==================== CLOSING ==================== -->
    <section class="section">
      <div class="container-page">
        <div class="panel-brand flex flex-col gap-6 p-8 md:flex-row md:items-center md:justify-between md:p-10">
          <div class="max-w-xl">
            <h2 class="text-2xl">Start with a message, not a payment</h2>
            <p class="mt-2 text-[15px] leading-relaxed">
              Shortlist two or three tutors, ask each the same question, and see which answer you trust.
              It costs nothing and it is the best half-hour you will spend on this.
            </p>
          </div>
          <div class="flex shrink-0 flex-col gap-3">
            <Link :href="tutors()"><Button size="lg" class="w-full">Find your tutor</Button></Link>
            <Link :href="becomeATutor()"><Button variant="quiet" size="lg" class="w-full">I want to teach</Button></Link>
          </div>
        </div>
      </div>
    </section>
  </AppLayout>
</template>
